import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { getFrontendConfigLoader } from '@doxyme/config';
import { type AlertDismissState, type AlertData } from './types';

export type AlertNotificationData = AlertData & { timeout?: number };

type AlertsSlice = {
  queue: AlertNotificationData[];
};

const dismissAlertHandlers: Map<string, AlertData['callback']> = new Map();

export const getInitialState = (): AlertsSlice => ({
  queue: [],
});

const dismissAlert = createAsyncThunk(
  'alerts/dismissAlert',
  async (payload: { id: string; state?: AlertDismissState }) => {
    const callback = dismissAlertHandlers.get(payload.id);

    if (callback && payload.state) {
      await callback(payload.state);

      dismissAlertHandlers.delete(payload.id);
    }
    return { id: payload.id };
  },
);

export const alertsSlice = createSlice({
  name: 'alerts',
  initialState: () => getInitialState(),
  reducers: {
    addAlert: (state, action: PayloadAction<AlertNotificationData>) => {
      const isDuplicateID = state.queue.some(
        (alert) => alert.id === action.payload.id,
      );

      if (isDuplicateID) {
        if (
          (getFrontendConfigLoader().get('ENV') as string) === 'development'
        ) {
          throw new Error(`[Alerts]: Duplicate id "${action.payload.id}"`);
        }
        return;
      }

      state.queue.push(action.payload);

      if (action.payload.callback) {
        dismissAlertHandlers.set(action.payload.id, action.payload.callback);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(dismissAlert.fulfilled, (state, action) => {
      const { id } = action.payload;
      const alertInQueue = state.queue.some(({ id: qid }) => qid === id);

      if (alertInQueue) {
        state.queue = state.queue.filter((alert) => alert.id !== id);
      }
    });
  },
});

export const { addAlert } = alertsSlice.actions;

export { dismissAlert };
